import React from 'react';

import styles from './CustomButton.module.scss';
import { Info } from 'assets';
import clsx from 'clsx';

interface ICustomButtonProps {
  title: string;
  handleClick: () => void;
  isIcon?: boolean;
  dark?: boolean;
  disabled?: boolean;
}

/**
 * The console's primary button.
 *
 * ## Why this is a `<button>` and not a `<div>`
 *
 * It used to be a `<div>` with an `onClick`. A mouse could press it, because
 * the handler fires on click regardless of the element -- but nothing else
 * could. A `<div>` is not focusable, so it never entered the tab order; it
 * carried no role, so a screen reader announced it as ordinary text; and with
 * no key handler, Enter and Space did nothing even if focus were forced onto
 * it.
 *
 * That made the console keyboard-unusable at 19 call sites, including the login
 * form's Submit. Someone filling a form by keyboard reached the end of it and
 * had to pick up the mouse.
 *
 * A native `<button>` brings focusability, the role, Enter/Space activation,
 * `:disabled` semantics and form participation for free, and correctly -- all
 * of which a `role="button" tabIndex={0} onKeyDown` shim only approximates.
 *
 * ## The reset exists to keep it looking identical
 *
 * A `<button>` is not a neutral box. It arrives with a border, a background,
 * padding, `box-sizing: border-box` and -- the one that actually catches
 * people -- a font that does NOT inherit from `body`. The reset block in
 * `CustomButton.module.scss` undoes exactly those, so this change is
 * behavioural only. Verified by screenshotting every page before and after and
 * diffing them pixel by pixel.
 */
export const CustomButton = ({
  title,
  handleClick,
  isIcon,
  dark,
  disabled,
}: ICustomButtonProps) => {
  return (
    <button
      // Not optional. A button inside a <form> defaults to type="submit", so
      // omitting this would make these submit the form and reload the page --
      // which the <div> never did.
      type="button"
      disabled={disabled}
      // Kept exactly as it was, including the fact that the gradient in
      // `.container` paints over this grey, so the default variant does not
      // visibly change when disabled. That is a pre-existing styling bug;
      // fixing it here would turn this into a visual change, which it is
      // deliberately not. The native `disabled` attribute already stops the
      // click, which is the part that matters.
      style={disabled ? { backgroundColor: 'gray', cursor: 'auto' } : {}}
      className={clsx(dark ? [styles.container, styles.dark] : styles.container)}
      onClick={handleClick}
    >
      {isIcon && (
        <div className={styles.iconContainer}>
          <Info />
        </div>
      )}
      {title}
    </button>
  );
};
